import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star, Menu, Close, Instagram } from '../mui-icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../hooks/useTranslation';
import './Header.css';
import logoIcon from '../assets/icon-no-name.webp';
import iconImage from '../assets/icon.webp';

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useTranslation();

  // Detectar scroll para efeito de blur
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Fechar menu ao trocar de rota
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  return (
    <>
      {/* Skip Link para Acessibilidade */}
      <a href="#main-content" className="skip-link" aria-label="Pular para o conteúdo principal">
        Pular para o conteúdo
      </a>
      
      <header className={`header ${isScrolled ? 'header-scrolled' : ''}`} role="banner">
        <div className="container header-container">
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <Menu />
          </button>

          {/* Logo Centralizado (Mobile) */}
          <Link to="/" className="logo-container mobile-logo" onClick={closeMobileMenu}>
            <span className="logo-name">personalnutri.app</span>
          </Link>

          {/* Logo com Ícone (Desktop) */}
          <Link to="/" className="logo-container desktop-logo" onClick={closeMobileMenu}>
            <img 
              src={logoIcon} 
              alt="PersonalNutri - Nutrição Inteligente com IA" 
              className="logo"
              width={120}
              height={103}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          {/* Ícone à Direita (Mobile) */}
          <Link to="/" className="mobile-icon" onClick={closeMobileMenu}>
            <img 
              src={logoIcon} 
              alt="PersonalNutri - Nutrição Inteligente com IA" 
              className="mobile-icon-img"
              width={40}
              height={34}
              loading="eager"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="nav-links desktop-nav" aria-label="Navegação principal">
            <Link 
              to="/" 
              className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}
            >
              {t('header.home')}
            </Link>
            <Link 
              to="/about" 
              className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`}
            >
              {t('header.about')}
            </Link>
            <Link 
              to="/nutrition" 
              className={`nav-link ${location.pathname === '/nutrition' ? 'active' : ''}`}
            >
              {t('header.nutrition')}
            </Link>
            <Link 
              to="/plans" 
              className={`nav-link ${location.pathname === '/plans' ? 'active' : ''}`}
            >
              {t('header.plans')}
            </Link>
            <Link 
              to="/support" 
              className={`nav-link ${location.pathname === '/support' ? 'active' : ''}`}
            >
              {t('header.support')}
            </Link>
            <Link 
              to="/partnerships" 
              className={`nav-link nav-link-featured ${location.pathname === '/partnerships' ? 'active' : ''}`}
            >
              <span className="featured-text">{t('header.partnerships')}</span>
              <Star className="featured-badge-header" sx={{ fontSize: 16 }} />
            </Link>
            <Link 
              to="/privacy" 
              className={`nav-link ${location.pathname === '/privacy' ? 'active' : ''}`}
            >
              {t('header.policy')}
            </Link>
          </nav>

          {/* Instagram + Language Switcher (Desktop) */}
          <div className="desktop-actions">
            <a 
              href="https://www.instagram.com/personalnutri.app/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="header-social-link"
              aria-label="Siga-nos no Instagram"
            >
              <Instagram sx={{ fontSize: 22 }} />
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={`mobile-sidebar ${isMobileMenuOpen ? 'mobile-sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo" onClick={closeMobileMenu}>
            <img 
              src={iconImage} 
              alt="PersonalNutri - Nutrição Inteligente com IA" 
              className="sidebar-logo-img"
              width={50}
              height={50}
              loading="lazy"
              decoding="async"
            />
          </Link>
          <button 
            className="sidebar-close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <Close />
          </button>
        </div>

        <nav className="sidebar-nav" aria-label="Menu de navegação mobile">
          <Link 
            to="/" 
            className={`sidebar-link ${location.pathname === '/' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.home')}</span>
          </Link>
          <Link 
            to="/about" 
            className={`sidebar-link ${location.pathname === '/about' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.about')}</span>
          </Link>
          <Link 
            to="/nutrition" 
            className={`sidebar-link ${location.pathname === '/nutrition' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.nutrition')}</span>
          </Link>
          <Link 
            to="/plans" 
            className={`sidebar-link ${location.pathname === '/plans' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.plans')}</span>
          </Link>
          <Link 
            to="/support" 
            className={`sidebar-link ${location.pathname === '/support' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.support')}</span>
          </Link>
          <Link 
            to="/partnerships" 
            className={`sidebar-link sidebar-link-featured ${location.pathname === '/partnerships' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span className="featured-text">{t('header.partnerships')}</span>
            <Star className="featured-badge" sx={{ fontSize: 16 }} />
          </Link>
          <Link 
            to="/privacy" 
            className={`sidebar-link ${location.pathname === '/privacy' ? 'active' : ''}`}
            onClick={closeMobileMenu}
          >
            <span>{t('header.policy')}</span>
          </Link>
        </nav>

        {/* Social + Language Switcher (Mobile) */}
        <div className="mobile-social-links">
          <a 
            href="https://www.instagram.com/personalnutri.app/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="mobile-social-link"
            aria-label="Siga-nos no Instagram"
          >
            <Instagram sx={{ fontSize: 20 }} />
            <span>{t('footer.followUs') || 'Siga-nos'}</span>
          </a>
        </div>
        <div className="mobile-language-switcher">
          <LanguageSwitcher />
        </div>
      </aside>
    </>
  );
};

export default Header;

